import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { WebhooksAPI } from "./webhooks.api";
import {
  GetWebhooksQuery,
  GetWebhooksResponse,
  WebHook,
} from "./webhooks.types";
import { useAuth } from "@/contexts/AuthContext";

const webhookKeys = {
  all: ["webhooks", "list"] as const,
  details: (uuid: string) => ["webhooks", "list", { uuid }] as const,
};

export const useGetWebhook = (query?: GetWebhooksQuery) => {
  return useQuery<GetWebhooksResponse>({
    queryKey: query ? [...webhookKeys.all, query] : webhookKeys.all,
    queryFn: () => WebhooksAPI.get(query),
  });
};

export const useCreateWebhook = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: WebhooksAPI.create,
    onMutate: async (newItem) => {
      const now = new Date().toISOString();

      const newWebhook: WebHook = {
        uuid: newItem.uuid,
        name: newItem.name,
        description: newItem?.description ?? "",
        url: newItem.url,
        events: newItem.events,
        secret: newItem.secret,
        id: undefined,
        created_by: {
          user_id: currentUser?.user_id,
          full_name: currentUser?.full_name,
          email: currentUser?.email,
        },
        created_on: now,
        updated_on: now,
        enabled: true,
      };

      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: webhookKeys.all });

      // Snapshot the previous value
      const previous = queryClient.getQueryData(webhookKeys.all);

      // Optimistically update to the new value
      queryClient.setQueryData(
        webhookKeys.all,
        (old: GetWebhooksResponse | undefined) => ({
          ...old,
          data: [...(old?.data ?? []), newWebhook],
        })
      );

      // Create cache for the new item
      queryClient.setQueryData(webhookKeys.details(newWebhook.uuid), () => ({
        data: [newWebhook],
      }));

      // Return a context object with the snapshotted value
      return { previous };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newItem, context) => {
      queryClient.setQueryData(webhookKeys.all, context?.previous);
      queryClient.removeQueries({
        queryKey: webhookKeys.details(newItem.uuid),
      });
    },
    // Always refetch after error or success:
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: webhookKeys.all }),
  });

  return mutation;
};

export const useUpdateWebhook = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: WebhooksAPI.update,
    onMutate: async (newItem) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: webhookKeys.details(newItem.uuid),
      });

      // Snapshot the previous value
      const previous: GetWebhooksResponse | undefined =
        queryClient.getQueryData(webhookKeys.details(newItem.uuid));

      if (!previous) {
        return;
      }

      const updatedWebhook: WebHook = {
        name: newItem.name || previous?.data?.[0]?.name,
        description: newItem.description || previous?.data?.[0]?.description,
        url: newItem.url || previous?.data?.[0]?.url,
        events: newItem.events || previous?.data?.[0]?.events,
        enabled: newItem.enabled ?? previous?.data?.[0]?.enabled,
        id: previous?.data?.[0]?.id,
        uuid: previous?.data?.[0]?.uuid,
        secret: previous?.data?.[0]?.secret,
        created_by: previous?.data?.[0]?.created_by,
        created_on: previous?.data?.[0]?.created_on,
        updated_on: new Date().toISOString(),
      };

      // Optimistically update to the new value
      queryClient.setQueryData(webhookKeys.details(newItem.uuid), {
        data: [updatedWebhook],
      });

      // Update the list of webhooks
      queryClient.setQueryData(webhookKeys.all, (old: GetWebhooksResponse) => {
        const updatedList = old?.data?.map((webhook) => {
          if (webhook.uuid === newItem.uuid) {
            return updatedWebhook;
          }
          return webhook;
        });

        return { ...old, data: updatedList };
      });

      // Return a context with the previous and new todo
      return { previous, newItem: updatedWebhook };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newItem, context) => {
      queryClient.setQueryData(webhookKeys.all, context?.previous);
    },
    // Always refetch after error or success:
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: webhookKeys.all }),
  });

  return mutation;
};

export const useDeleteWebhook = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: WebhooksAPI.delete,
    onMutate: async (uuid) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: webhookKeys.all });

      // Snapshot the previous value
      const previous = queryClient.getQueryData(webhookKeys.all);

      // Optimistically update to the new value
      queryClient.setQueryData(webhookKeys.all, (old: GetWebhooksResponse) => ({
        ...old,
        data: old?.data?.filter((webhook) => webhook.uuid !== uuid),
      }));

      // Return a context object with the snapshotted value
      return { previous };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, uuid, context) => {
      queryClient.setQueryData(webhookKeys.all, context?.previous);
    },
    // Always refetch after error or success:
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: webhookKeys.all }),
  });

  return mutation;
};
